
<script>
import Repositories from '@/repositories';
const StepsRepository = Repositories.get('steps');
const CategoryTypesRepository = Repositories.get('categorytypes');
import Multiselect from 'vue-multiselect';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import HeaderContentLayout from '@/router/layouts/header-content-layout';
import SharedUrlButton from '@/components/widgets/buttons/shared-url';

export default {
  page: {
    title: 'CategoryTypes',
  },
  components: {
    Loading,
    Multiselect,
    HeaderContentLayout,
    SharedUrlButton,
  },
  data() {
    return {
      isLoading: false,
      steps: [],
      categoryType: null,
    };
  },
  async created() {
    await this.loadData();
  },
  methods: {
    async loadData() {
      try {
        this.isLoading = true;

        let stepsPromise = StepsRepository.getAll();

        let categoryTypePromise = CategoryTypesRepository.get(
          this.$route.params.idPublic
        );

        const [steps, categoryType] = await Promise.all([
          stepsPromise,
          categoryTypePromise,
        ]);

        await this.loadSteps(steps);
        await this.loadCategoryType(categoryType);
      } catch {
        this.$toast.errorToast(this.$t('errors.errorLoading'));
      } finally {
        this.isLoading = false;
      }
    },
    async loadSteps(stepsResponse) {
      this.steps = stepsResponse.data.data.sort(function (a, b) {
        return a.generalOrder - b.generalOrder;
      });
    },
    async loadCategoryType(categoryTypeResponse) {
      if (categoryTypeResponse == null) {
        this.categoryType = null;
        return;
      }

      let categoryType = categoryTypeResponse.data;

      categoryType.consumptionStep = this.steps.find(
        (x) => x.idPublic == categoryType.consumptionStepIdPublic
      );

      categoryType.referenceDate = this.referenceDates.find(
        (x) => x.value == categoryType.referenceDate
      );

      this.categoryType = categoryType;
    },
    async update() {
      try {
        this.isLoading = true;

        let apiWrapper = {
          id: this.categoryType.id,
          idPublic: this.categoryType.idPublic,
          name: this.categoryType.name,
          leadTime: this.categoryType.leadTime,
          referenceDate: this.categoryType.referenceDate?.value,
          consumptionStepIdPublic: this.categoryType.consumptionStep?.idPublic,
          sharedUrlKey: this.categoryType.sharedUrlKey,
        };

        await CategoryTypesRepository.update(
          apiWrapper,
          this.$route.params.idPublic
        );

        this.$toast.successToast(this.$t('general.success'));

        this.$router.push({ name: 'categoryTypesConfig' });
      } catch {
        this.$toast.errorToast(this.$t('errors.errorLoading'));
      } finally {
        this.isLoading = false;
      }
    },
    cancel() {
      this.$router.push({ name: 'categoryTypesConfig' });
    },
    sharedUrlKeyUpdated(sharedUrlKey) {
      this.categoryType.sharedUrlKey = sharedUrlKey;
    },
  },
  computed: {
    sharedUrl() {
      if (
        this.categoryType == null ||
        this.categoryType.id == null ||
        this.categoryType.id <= 0
      )
        return null;

      return (
        process.env.VUE_APP_REDIRECT_URL +
        'shared/' +
        this.$store.state.user.config.organisationCode +
        '/consumption/categorytypes/' +
        this.categoryType.id
      );
    },
    sharedUrlRepo() {
      return CategoryTypesRepository;
    },
    referenceDates() {
      return [
        {
          name: this.$t('general.installationDate'),
          value: 'installationDate',
        },
        {
          name: this.$t('general.deliveryDate'),
          value: 'deliveryDate',
        },
        {
          name: this.$t('general.productionDate'),
          value: 'productionDate',
        },
      ];
    },
  },
};
</script>

<template>
  <HeaderContentLayout
    @ctrl-enter-pressed="update"
    :title="$t('general.edit')"
    :overflow="false"
  >
    <template v-slot:header>
      <div class="flex-box">
        <b-button type="submit" variant="success" class="mr-2" @click="update">
          {{ $t('general.save') }}
        </b-button>
        <b-button
          @click="cancel()"
          tabindex="-1"
          type="button"
          variant="secondary"
        >
          {{ $t('general.cancel') }}
        </b-button>
        <div class="flex-push">
          <SharedUrlButton
            v-if="sharedUrl != null"
            :repository="sharedUrlRepo"
            :objectId="categoryType.id"
            target="categoryTypeSharedLink"
            :baseSharedUrl="sharedUrl"
            :baseSharedKey="categoryType.sharedUrlKey"
            @shared-url-key-updated="sharedUrlKeyUpdated"
            :title="categoryType.name"
          />
        </div>
      </div>
    </template>

    <loading
      :active.sync="isLoading"
      :can-cancel="false"
      color="#f1b44c"
      :width="70"
      :height="70"
      loader="dots"
      :is-full-page="false"
    >
    </loading>

    <b-form v-if="categoryType != null" style="max-width: 1000px">
      <h2>{{ $t(`categories.types.${categoryType.name.toLowerCase()}`) }}</h2>

      <b-row>
        <b-col>
          <b-form-group
            id="group-consumption-steps"
            :label="$t('categories.consumptionStep')"
            label-for="consumptionStepInput"
          >
            <multiselect
              id="consumptionStepInput"
              v-model="categoryType.consumptionStep"
              :options="steps"
              :show-labels="false"
              :searchable="true"
              :label="'name'"
              track-by="idPublic"
              :placeholder="$t('general.pickAValue')"
            >
              <template v-slot:noOptions>
                {{ $t('general.noResult') }}
              </template>
            </multiselect>
          </b-form-group>
        </b-col>
        <b-col>
          <b-form-group
            id="group-lead-time"
            :label="$t('general.leadTime')"
            label-for="leadTimeInput"
          >
            <b-form-input type="number" v-model="categoryType.leadTime" />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <b-form-group
            id="group-reference-date"
            :label="$t('general.referenceDate')"
            label-for="referenceDateInput"
          >
            <multiselect
              id="referenceDateInput"
              v-model="categoryType.referenceDate"
              :options="referenceDates"
              :show-labels="false"
              :searchable="true"
              :label="'name'"
              track-by="value"
              :placeholder="$t('general.pickAValue')"
            >
              <template v-slot:noOptions>
                {{ $t('general.noResult') }}
              </template>
            </multiselect>
          </b-form-group>
        </b-col>
      </b-row>
    </b-form>
  </HeaderContentLayout>
</template>

<style scoped>
.group-box {
  border-radius: 5px;
  min-height: 200px;
  overflow-x: hidden;
  overflow-y: hidden;
  padding: 15px 20px;
  margin-bottom: 16px;
  background-color: #f5f5f5;
}
</style>